import { Controller } from '@hotwired/stimulus';
import { subscribeTo } from '../channels/sales_team_channel';

export default class extends Controller {
  static targets = ['feed', 'input', 'reviewPanel', 'statusLegend'];
  // inTraining: the Drafts page sets this while the team is in its training
  // period — every edit then needs a one-line comment (server enforces it).
  static values = { teamId: Number, inTraining: Boolean };

  connect() {
    // Subscribe to real-time updates
    if (this.teamIdValue) {
      subscribeTo(this.teamIdValue);
    }

    // Scroll feed to bottom
    if (this.hasFeedTarget) {
      this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
    }

    // Enter key sends chat
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          this.sendChat();
        }
      });
    }

    // Listen for team status broadcasts (working/done)
    this._statusHandler = (event) => this.handleTeamStatus(event.detail);
    document.addEventListener('sales-team:status', this._statusHandler);

    // Delegated click handler for SQL toggle buttons (works for dynamic items too)
    this._sqlToggleHandler = (event) => {
      const toggle = event.target.closest('.activity-sql-toggle');
      if (!toggle) return;
      const item = toggle.closest('.activity-item');
      if (!item) return;
      const detail = item.querySelector('.activity-sql-detail');
      if (!detail) return;
      const isHidden = detail.style.display === 'none';
      detail.style.display = isHidden ? 'block' : 'none';
      toggle.textContent = isHidden ? 'Hide SQL' : 'SQL';
    };
    document.addEventListener('click', this._sqlToggleHandler);
  }

  disconnect() {
    if (this._statusHandler) {
      document.removeEventListener('sales-team:status', this._statusHandler);
    }
    if (this._sqlToggleHandler) {
      document.removeEventListener('click', this._sqlToggleHandler);
    }
    if (this._workingTimeout) {
      clearTimeout(this._workingTimeout);
    }
  }

  handleTeamStatus(data) {
    if (data.status === 'working') {
      this.showWorkingIndicator();
    } else if (data.status === 'done' || data.status === 'waiting') {
      // 'waiting' = paused on draft backlog — the team is NOT working,
      // so don't leave the indicator stuck until the safety timeout.
      this.hideWorkingIndicator();
    }
  }

  showWorkingIndicator() {
    this.hideWorkingIndicator();

    const indicator = document.createElement('div');
    indicator.id = 'sales-team-working';
    indicator.classList.add('activity-item', 'activity-working');
    indicator.innerHTML = `
      <div class="activity-content">
        <span class="working-dots"><span></span><span></span><span></span></span>
        Team is working&hellip;
      </div>
    `;
    if (this.hasFeedTarget) {
      this.feedTarget.appendChild(indicator);
      this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
    }

    this.setInputWorking(true);

    // Safety net: re-enable after 5 minutes in case done signal is lost
    this._workingTimeout = setTimeout(() => this.hideWorkingIndicator(), 300000);
  }

  hideWorkingIndicator() {
    const indicator = document.getElementById('sales-team-working');
    if (indicator) indicator.remove();

    this.setInputWorking(false);

    if (this._workingTimeout) {
      clearTimeout(this._workingTimeout);
      this._workingTimeout = null;
    }
  }

  setInputWorking(working) {
    if (this.hasInputTarget) {
      // Never disable the input — the owner can always send instructions
      this.inputTarget.placeholder = working
        ? 'Team is working... send another instruction anytime'
        : 'Give the sales manager an instruction';
    }
    const sendBtn = this.hasInputTarget &&
      this.inputTarget.closest('.input-group')?.querySelector('button');
    if (sendBtn) {
      sendBtn.disabled = false;
      sendBtn.textContent = 'Send';
    }
  }

  sendChat() {
    const message = this.inputTarget.value.trim();
    if (message === '') return;

    // Show the owner's message in the feed immediately
    const feedItem = document.createElement('div');
    feedItem.classList.add('activity-item', 'activity-owner');
    feedItem.innerHTML = `
      <div class="activity-role">You</div>
      <div class="activity-content">${this.escapeHtml(message)}</div>
    `;
    this.feedTarget.appendChild(feedItem);
    this.feedTarget.scrollTop = this.feedTarget.scrollHeight;

    // Clear input and show working indicator
    this.inputTarget.value = '';
    this.showWorkingIndicator();

    // Send to backend
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    fetch('/sales_team/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ message: message }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.error) {
          this.showFeedError('Chat error: ' + data.error);
          this.hideWorkingIndicator();
        }
      })
      .catch(error => {
        console.error('Error:', error);
        this.showFeedError('Failed to send message: ' + error.message);
        this.hideWorkingIndicator();
      });
      // Input stays disabled until team_status: done arrives via ActionCable
  }

  showFeedError(message) {
    const errorItem = document.createElement('div');
    errorItem.classList.add('activity-item', 'activity-error');
    errorItem.innerHTML = `
      <div class="activity-role">Error</div>
      <div class="activity-content" style="color: #d9534f;">${this.escapeHtml(message)}</div>
    `;
    this.feedTarget.appendChild(errorItem);
    this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
  }

  refreshFeed() {
    window.location.reload();
  }

  async approveAll(event) {
    const button = event.currentTarget;
    const cards = Array.from(this.element.querySelectorAll('.email-card'));
    if (!cards.length || !window.confirm(`Send all ${cards.length} drafts?`)) return;

    button.disabled = true;
    let approved = 0;
    for (const card of cards) {
      if (await this.sendDraft(card)) approved += 1;
    }
    const result = this.element.querySelector('[data-approval-result]');
    result.textContent = `${approved} approved and queued for sending. ${cards.length - approved} still waiting.`;
    button.disabled = false;
    button.hidden = !this.element.querySelector('.email-card');
  }

  async approveEmail(event) {
    await this.sendDraft(document.getElementById(`email-card-${event.currentTarget.dataset.emailId}`));
  }

  async sendDraft(card) {
    if (!card || card.dataset.sending) return false;
    card.dataset.sending = 'true';
    const controls = Array.from(card.querySelectorAll('button, select'));
    controls.forEach(control => { control.disabled = true; });
    const emailId = card.id.replace('email-card-', '');
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const sigEl = card.querySelector('.email-card-signature-id');
    card.querySelector('.approval-error')?.remove();
    try {
      const response = await fetch('/sales_team/approve_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ email_id: emailId, signature_id: sigEl?.value }),
      });
      const data = await response.json().catch(() => ({ error: 'Could not approve this draft. Refresh the page before retrying.' }));
      if (!response.ok || data.status !== 'approved') {
        throw new Error(data.error || 'Could not approve this draft. Please try again.');
      }
      const section = card.closest('.drafts-section');
      card.remove();
      if (section) {
        const remaining = section.querySelectorAll('.email-card').length;
        const badge = section.querySelector('.email-count-badge');
        if (badge) badge.textContent = remaining;
        if (!remaining && section.tagName === 'DETAILS') section.remove();
      }
      this.updateEmailBadge();
      return true;
    } catch (error) {
      const message = document.createElement('p');
      message.className = 'approval-error text-danger';
      message.setAttribute('role', 'alert');
      message.textContent = error.message;
      card.appendChild(message);
      card.closest('details')?.setAttribute('open', '');
      return false;
    } finally {
      delete card.dataset.sending;
      controls.forEach(control => { control.disabled = false; });
    }
  }

  rejectEmail(event) {
    const emailId = event.currentTarget.dataset.emailId;
    const card = document.getElementById(`email-card-${emailId}`);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    fetch('/sales_team/reject_email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ email_id: emailId }),
    })
      .then(response => response.json())
      .then(data => {
        if (card) {
          card.classList.add('email-card-rejected');
          setTimeout(() => { card.remove(); this.updateEmailBadge(); }, 500);
        }
      })
      .catch(error => console.error('Error:', error));
  }

  editEmail(event) {
    const emailId = event.currentTarget.dataset.emailId;
    const card = document.getElementById(`email-card-${emailId}`);
    if (!card) return;

    const subject = event.currentTarget.dataset.emailSubject;
    const body = event.currentTarget.dataset.emailBody;

    const subjectEl = card.querySelector('[data-field="subject"]');
    const bodyEl = card.querySelector('[data-field="body"]');
    const actionsEl = card.querySelector('.email-card-actions');

    // Convert plain text to HTML if needed (for older plain-text bodies)
    let htmlBody = body;
    if (!body.includes('<p>') && !body.includes('<br')) {
      const paragraphs = body.split(/\n\n+/);
      htmlBody = paragraphs.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('\n');
    }

    subjectEl.innerHTML = `<input type="text" class="form-control input-sm email-edit-subject" value="${this.escapeAttr(subject)}">`;

    bodyEl.innerHTML = `
      <div class="email-edit-toolbar" style="margin-bottom: 6px;">
        <div class="btn-group">
          <button type="button" class="btn btn-sm btn-default" data-cmd="bold" title="Bold (Ctrl+B)">
            <i class="glyphicon glyphicon-bold"></i>
          </button>
          <button type="button" class="btn btn-sm btn-default" data-cmd="italic" title="Italic (Ctrl+I)">
            <i class="glyphicon glyphicon-italic"></i>
          </button>
          <button type="button" class="btn btn-sm btn-default" data-cmd="underline" title="Underline (Ctrl+U)">
            <b><u>U</u></b>
          </button>
          <button type="button" class="btn btn-sm btn-default" data-cmd="createLink" title="Insert link">
            <i class="glyphicon glyphicon-link"></i>
          </button>
        </div>
      </div>
      <div class="email-edit-body" contenteditable="true"
           style="border: 1px solid #ccc; border-radius: 4px; padding: 8px; min-height: 120px; background: #fff; font-size: 13px; line-height: 1.5;">
        ${htmlBody}
      </div>
      <div class="email-edit-feedback" style="margin-top: 10px;">
        <label style="font-size: 12px; color: #888;">Feedback for the AI <span style="font-weight: normal;">${this.inTrainingValue ? '(one line on what you changed and why — required during training)' : '(optional — helps the AI write better emails next time)'}</span></label>
        <textarea class="form-control email-edit-comment" rows="2" placeholder="e.g. &quot;Too formal, make it more casual&quot; or &quot;Wrong class name&quot;"></textarea>
      </div>
    `;

    // Wire up toolbar buttons using native execCommand
    bodyEl.querySelectorAll('[data-cmd]').forEach(btn => {
      btn.addEventListener('mousedown', (e) => e.preventDefault()); // keep focus in editor
      btn.addEventListener('click', () => {
        const cmd = btn.dataset.cmd;
        if (cmd === 'createLink') {
          const url = prompt('Enter link URL:', 'https://');
          if (url) document.execCommand('createLink', false, url);
        } else {
          document.execCommand(cmd, false, null);
        }
      });
    });

    // Replace action buttons with Save / Cancel
    actionsEl.innerHTML = `
      <button class="btn btn-primary btn-sm" data-action="click->sales-team#saveEmail" data-email-id="${emailId}">Save</button>
      <button class="btn btn-default btn-sm" data-action="click->sales-team#cancelEditEmail" data-email-id="${emailId}">Cancel</button>
    `;
  }

  saveEmail(event) {
    const emailId = event.currentTarget.dataset.emailId;
    const card = document.getElementById(`email-card-${emailId}`);
    if (!card) return;

    const subject = card.querySelector('.email-edit-subject').value;
    const commentEl = card.querySelector('.email-edit-comment');
    const comment = (commentEl?.value || '').trim();
    const body = card.querySelector('.email-edit-body').innerHTML;

    if (this.inTrainingValue && !comment && commentEl) {
      commentEl.focus();
      alert('While the team is in training, add a one-line note on what you changed and why.');
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    fetch('/sales_team/update_email', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ email_id: emailId, subject: subject, body: body, owner_comment: comment }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === 'updated') {
          const subjectEl = card.querySelector('[data-field="subject"]');
          const bodyEl = card.querySelector('[data-field="body"]');
          const actionsEl = card.querySelector('.email-card-actions');

          subjectEl.textContent = subject;
          bodyEl.innerHTML = body;

          actionsEl.innerHTML = `
            <button class="btn btn-success btn-sm" data-action="click->sales-team#approveEmail" data-email-id="${emailId}">Approve</button>
            <button class="btn btn-info btn-sm" data-action="click->sales-team#editEmail" data-email-id="${emailId}" data-email-subject="${this.escapeAttr(subject)}" data-email-body="${this.escapeAttr(body)}">Edit</button>
            <button class="btn btn-default btn-sm" data-action="click->sales-team#rejectEmail" data-email-id="${emailId}">Reject</button>
          `;
        } else if (data.error) {
          alert(data.error);
        }
      })
      .catch(error => console.error('Error:', error));
  }

  cancelEditEmail(event) {
    // Reload the page to restore original state
    window.location.reload();
  }

  updateEmailBadge() {
    const emailCards = document.getElementById('sales-email-cards');
    const badge = document.getElementById('email-count-badge');
    if (!emailCards || !badge) return;

    const count = emailCards.querySelectorAll('.email-card').length;
    if (count > 0) {
      badge.textContent = count;
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  }

  toggleStatusLegend() {
    if (!this.hasStatusLegendTarget) return;
    const el = this.statusLegendTarget;
    el.style.display = (el.style.display === 'none' || !el.style.display) ? 'block' : 'none';
  }

  switchPlayTab(event) {
    const tab = event.currentTarget.dataset.tab;
    const playQueue = document.getElementById('play-queue');
    if (!playQueue) return;

    // Toggle tab buttons
    document.querySelectorAll('.play-tab').forEach(btn => btn.classList.remove('play-tab-active'));
    event.currentTarget.classList.add('play-tab-active');

    // Toggle tab content
    const currentPanel = playQueue.querySelector('.play-tab-current');
    const pastPanel = playQueue.querySelector('.play-tab-past');
    if (tab === 'past') {
      if (currentPanel) currentPanel.style.display = 'none';
      if (pastPanel) pastPanel.style.display = '';
    } else {
      if (currentPanel) currentPanel.style.display = '';
      if (pastPanel) pastPanel.style.display = 'none';
    }
  }

  escapeAttr(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
